import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import makeStyles from '@mui/styles/makeStyles';
import {
  setSelectedVessel,
  selectedVesselId,
  fleetNavigationData,
} from "../../model/slice/fleetNavigationDataSlice";
import { reset, setCurrentChannelPage } from "../../model/slice/channelSlice";
import AlarmTopNavigation from "./navigation/top.jsx";
import { useDispatch, useSelector } from "react-redux";
import { ChannelList } from "./list.jsx";
import { TabSelector } from "../tabSelector/tabSelector.jsx";
import localizationKeys from "../../i18n/localizationKeys";
import { MobileListBottomNavigation } from "../common/listUtil/mobileListBottom.jsx";
import ChannelGroupSelector from "./channelGroupSelector.jsx";
import { COLORS } from "../../constants/colors.js";
import { ChannelGroupRegisterPage } from "./channelGroupRegisterPage.jsx";
import { Confirm } from "../trendPage/customGraph/modal/confirm.jsx";
import EditIcon from "../../../assets/images/alarmIcon/edit-icon.svg";
import DeleteIcon from "../trendPage/icon/deleteIcon.jsx";
import { GroupSelectDialog } from "./utils/goupSelectDialog.jsx";
import { RemoveSelectedChannelsDialog } from "./utils/removeSelectedChannelsDialog.jsx";
import { deleteChannelGroup, updateChannelGroup } from "../../api/channel.js";

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    color: "#ffffff",
    paddingTop: "12px",
    [theme.breakpoints.down('md')]: {
      justifyContent: "center",
    },
  },
  tabHeaderSpace: {
    marginLeft: "0px",
    [theme.breakpoints.down('sm')]: {
      marginTop: "-90px",
    },
  },
  table: {
    padding: "0px 8px !important",
    marginRight: "24px",
    [theme.breakpoints.down('md')]: {
      overflow: "auto",
      marginTop: "180px",
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: "10px",
      height: "calc(100dvh - 190px)",
    },
  },
}));

const commonTabs = (t) => [
  {
    name: t(localizationKeys.ChannelData_upper),
    id: "channelData",
  },
];

export const defaultChannelGroups = [{
  id: 'AllChannel',
  name: 'All Channel',
  values: [],
}]

const ChannelPage = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const dispatch = useDispatch();
  const vesselId = useSelector(selectedVesselId);
  const fleetNavData = useSelector(fleetNavigationData);
  const [filterWord, setFilterWord] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [containerHeight, setContainerHeight] = useState((window.innerHeight ?? 380) - 230);
  const [currentVesselId, setCurrentVesselId] = useState(null);
  const [channelGroups, setChannelGroups] = useState([]);
  const [selectedChannelGroup, setSelectedChannelGroup] = useState(defaultChannelGroups[0]);
  const [isCreatingNewGroup, setIsCreatingNewGroup] = useState(false);
  const [isEditingGroup, setIsEditingGroup] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptContent, setPromptContent] = useState({});
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [groupSelectDialogOpen, setGroupSelectDialogOpen] = useState(false);
  const [removeChannelsFromGroupDialogOpen, setRemoveChannelsFromGroupDialogOpen] = useState(false);
  const previousSelectedChannelGroup = useRef(null);
  const [isUpdateFetch, setIsUpdateFetch] = useState(false);
  
  const clearList = () => {
    setCurrentVesselId(null);
    dispatch(setSelectedVessel(null));
    dispatch(reset());
  };

  const openGroupRegisterPage = () => {
      const newChannelGroup = {name: `New Custom Group-${channelGroups.length}`, values: []};
      previousSelectedChannelGroup.current = channelGroups[0];
      setIsCreatingNewGroup(true);
      setChannelGroups([...channelGroups, newChannelGroup]);
      setSelectedChannelGroup(newChannelGroup);
  }

  const handleGroupSelectorDialogClose = () => {
    setGroupSelectDialogOpen(false);
  }

  const handleGroupSelectorDialogOnOK = async (selectedGroup) => {
    const targetGroup = channelGroups.find((group) => group.id === selectedGroup.id);
    const newGroup = {...targetGroup, values: [...new Set([...targetGroup.values, ...selectedChannels])]};
    setSelectedChannels([]);
    await updateChannelGroup(vesselId, newGroup.id, newGroup);
    setSelectedChannelGroup(newGroup);
    setGroupSelectDialogOpen(false);
    setIsUpdateFetch(true);
  }

  const handleRemoveChannelsDialogClose = () => {
    setRemoveChannelsFromGroupDialogOpen(false);
  }

  const handleRemoveChannelsDialogOnDelete = async () => {
    await deleteChannelGroup(vesselId, selectedChannelGroup.id);
    setSelectedChannelGroup(defaultChannelGroups[0]);
    setSelectedChannels([]);
    setRemoveChannelsFromGroupDialogOpen(false);
    setIsUpdateFetch(true);
  }

  const handleRemoveChannelsDialogOnKeep = async () => {
    setSelectedChannels([]);
    await updateChannelGroup(vesselId, selectedChannelGroup.id, {name: selectedChannelGroup.name, values: []})
    setSelectedChannelGroup(selectedChannelGroup);
    setRemoveChannelsFromGroupDialogOpen(false);
    setIsUpdateFetch(true);
  }
  const handleRemoveChannels = async () => {
    const targetGroup = channelGroups.find((group) => group.id === selectedChannelGroup.id)
    const newGroup = {...targetGroup, values: targetGroup.values.filter((chNo) => !selectedChannels.includes(chNo))}
    setSelectedChannels([]);
    await updateChannelGroup(vesselId, newGroup.id, newGroup);
    setSelectedChannelGroup(newGroup);
    setIsUpdateFetch(true);
  }

  const handleClickOnDelete = () => {
    const isSelectedAll = selectedChannels.length === selectedChannelGroup.values.length;
    if(isSelectedAll){
      setRemoveChannelsFromGroupDialogOpen(true);
    }else{
      handleRemoveChannels();
    }
  }

  useEffect(() => {
    const vesselPath = location.pathname.split("/")?.[2] ?? null;
    if (vesselPath) {
      dispatch(setSelectedVessel(vesselPath));
    } else {
      clearList();
    }
    previousSelectedChannelGroup.current = selectedChannelGroup;
    const handleResize = () => {
      setContainerHeight(window.innerHeight - 230);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  useEffect(() => {
    setCurrentVesselId(vesselId);
    dispatch(setCurrentChannelPage(0));
  }, [vesselId]);

  useEffect(() => {
    if (fleetNavData?.fleetNavigationData?.length === 1) {
      setCurrentVesselId(fleetNavData.fleetNavigationData[0].vessel_id);
    }
  }, [fleetNavData]);


  return (
    <>
      {isSmUp && (
        <TabSelector tabs={commonTabs(t)} selectedOne={"channelData"} clickHandler={() => {}} />
      )}
      <Confirm
        when={showPrompt}
        title={t(promptContent.title)}
        message={t(promptContent.message)}
        cancelText={t(promptContent.cancelText)}
        okText={t(promptContent.okText)}
        onOK={promptContent.okHandler}
        onCancel={promptContent.cancelHandler}
        isDelete={promptContent.isDelete}
      />
      <GroupSelectDialog 
        channelGroups={channelGroups} 
        open={groupSelectDialogOpen} 
        handleClose={handleGroupSelectorDialogClose} 
        handleOk={handleGroupSelectorDialogOnOK}
      />
      <RemoveSelectedChannelsDialog
        open={removeChannelsFromGroupDialogOpen}
        handleClose={handleRemoveChannelsDialogClose}
        handleKeep={handleRemoveChannelsDialogOnKeep}
        handleDelete={handleRemoveChannelsDialogOnDelete}
      />
      <div style={{
        height: "50px",
        width: "100%",
        position: "fixed",
        top: "98px",
        display: "flex",
        backgroundColor: COLORS.backgroundColor,
        zIndex: 999,
      }}>
        <ChannelGroupSelector
          vesselId={currentVesselId}
          channelGroups={channelGroups}
          setChannelGroups={setChannelGroups}
          selectedChannelGroup={selectedChannelGroup}
          setSelectedChannelGroup={setSelectedChannelGroup}
          isCreatingNewGroup={isCreatingNewGroup}
          setIsCreatingNewGroup={setIsCreatingNewGroup}
          previousSelectedChannelGroup={previousSelectedChannelGroup}
          isEditingGroup={isEditingGroup}
          setIsEditingGroup={setIsEditingGroup}
          setSelectedChannels={setSelectedChannels}
          isUpdateFetch={isUpdateFetch}
          setIsUpdateFetch={setIsUpdateFetch}
        />
      </div>
      {
        (isCreatingNewGroup || isEditingGroup) ? 
        <ChannelGroupRegisterPage 
          vesselId={vesselId} 
          channelGroups={channelGroups}
          setChannelGroups={setChannelGroups}
          isCreatingNewGroup={isCreatingNewGroup}
          setIsCreatingNewGroup={setIsCreatingNewGroup}
          selectedChannelGroup={selectedChannelGroup}
          setSelectedChannelGroup={setSelectedChannelGroup} 
          previousSelectedChannelGroup={previousSelectedChannelGroup}
          isEditingGroup={isEditingGroup}
          setIsEditingGroup={setIsEditingGroup}
          setShowPrompt={setShowPrompt}
          setPromptContent={setPromptContent}
          selectedChannels={selectedChannels}
          setSelectedChannels={setSelectedChannels}
          setIsUpdateFetch={setIsUpdateFetch}
        />
      :(
        <div className={styles.root}>
          <Grid container sx={{marginLeft: "12px", position: "relative", zIndex: 100}}>
            <Typography fontSize={20} sx={{color: "white"}}>
              {selectedChannelGroup.name}
            </Typography>
            {selectedChannelGroup?.id !== defaultChannelGroups[0].id && (
              <Box
                sx={{ maxWidth: "30px", width: "30px", alignContent: "end", marginLeft: "16px", cursor: "pointer"}}
                onClick={() => {
                  setSelectedChannels(selectedChannelGroup.values);
                  setIsEditingGroup(true);
                }}
              >
                <img
                  src={EditIcon}
                  style={{color: "white"}}
                />
              </Box>
            )}
          </Grid>
          {/* Favorites is not implemented yet */}
          <Grid container spacing={2} className={styles.tabHeaderSpace} alignItems="center">
            {isSmUp && (
              <AlarmTopNavigation
                filterWord={filterWord}
                setFilterWord={setFilterWord}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                vesselId={currentVesselId}
                selectedChannelGroup={selectedChannelGroup}
              />
            )}
            <Grid item size={{xs: 12}} className={styles.table}>
              <DisplayContent
                filterWord={filterWord}
                filterStatus={filterStatus}
                vesselId={currentVesselId}
                containerHeight={containerHeight}
                selectedChannelGroup={selectedChannelGroup}
                setIsEditingGroup={setIsEditingGroup}
                selectedChannels={selectedChannels}
                setSelectedChannels={setSelectedChannels}
              />
            </Grid>
            {!isSmUp && (
              <MobileListBottomNavigation
                filterWord={filterWord}
                setFilterWord={setFilterWord}
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                vesselId={currentVesselId}
              />
            )}
          </Grid>
        </div>
      )
      }
      {/* Footer for selected channels */}
      {
        selectedChannels.length > 0 && !(isCreatingNewGroup || isEditingGroup) && (
          <Grid container sx={{position: "sticky", bottom: 0, width: "100%", height: "96px", backgroundColor: "#aedfe0", padding: "24px 48px", alignItems: "center"}}>
            <Stack direction="row" spacing={2} sx={{width: "100%", justifyContent: "space-between"}}>
              <Stack direction="row" spacing={2}>
                <Box>
                  {`${selectedChannels.length} ${t(localizationKeys.ChannelGroupFooterChannels)}`}
                </Box>
                {
                  channelGroups.length > 1 && (
                    <Box onClick={() => {setGroupSelectDialogOpen(true)}} sx={{cursor: "pointer"}}>
                      {`+ ${t(localizationKeys.ChannelGroupFooterAddToGroupButton)}`}
                    </Box>
                  )
                }
                <Box sx={{cursor: "pointer", display: "flex", alignItems: "center"}} onClick={() => {openGroupRegisterPage()}}>
                  <Box sx={{width: "16px", height: "16px", margin: "0px 8px", maskRepeat: "no-repeat", maskPosition: "center", backgroundColor: "black", maskImage: `url(${EditIcon})`}}/>
                  {t(localizationKeys.ChannelGroupFooterMakeGroup)}
                </Box>
                {
                  selectedChannelGroup.id !== defaultChannelGroups[0].id &&
                  <Box onClick={handleClickOnDelete} sx={{cursor: "pointer", display: "flex", alignItems: "center"}}>
                    {/* <Box sx={{width: "16px", height: "16px", margin: "0px 8px", maskRepeat: "no-repeat", maskPosition: "center", backgroundColor: "black", maskImage: `url(${DeleteIcon})`}}/> */}
                    <DeleteIcon color="black" />
                    {t(localizationKeys.ChannelGroupFooterRemoveFromGroupButton)}
                  </Box>
                }
              </Stack>
              <Box sx={{cursor: "pointer"}} onClick ={() =>{setSelectedChannels([])}}>
                {t(localizationKeys.ChannelGroupFooterCancelButton)}
              </Box>
            </Stack>
          </Grid>
        )

      }
    </>
  );
};

const DisplayContent = (props) => {
  const { filterWord, filterStatus, vesselId, containerHeight, selectedChannelGroup, setIsEditingGroup, selectedChannels, setSelectedChannels } = props;
  return (
    <ChannelList
      filterValue={filterWord}
      filterStatus={filterStatus}
      vesselId={vesselId}
      height={containerHeight}
      selectedChannelGroup={selectedChannelGroup}
      setIsEditingGroup={setIsEditingGroup}
      selectedChannels={selectedChannels}
      setSelectedChannels={setSelectedChannels}
    />
  );
};

export { ChannelPage };
